/**
 * OutboundTable — T1 table for Ausgang and Gesendet tabs.
 * Ported from v0 kommunikation-view.tsx OutboundTable.
 * Reuses OUR Table primitives.
 */
import { AlertTriangle, RotateCw, Sparkles, PenLine } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { StatusDot } from '../../../components/ui/StatusDot';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../components/ui/Table';
import { EntityLink } from './EntityLink';
import { MailTeamBadge } from './MailTeamBadge';
import { mailTeamMarkerName } from '../mail-visibility';
import { OUT_STATUS } from '../mock';
import type { AusgangMail } from '../types';

function HerkunftBadge({ herkunft }: { herkunft: 'ki' | 'manuell' }) {
  if (herkunft === 'ki') {
    return (
      <Badge variant="info" className="gap-1">
        <Sparkles className="size-3" />
        KI-Entwurf
      </Badge>
    );
  }
  return (
    <Badge variant="neutral" className="gap-1">
      <PenLine className="size-3" />
      Manuell
    </Badge>
  );
}

interface OutboundTableProps {
  rows: AusgangMail[];
  onOpenDetail: (mail: AusgangMail) => void;
  onOpenKunde?: (kundeId: string) => void;
  onRetry?: (id: string) => void;
  emptyText: string;
}

export function OutboundTable({
  rows,
  onOpenDetail,
  onOpenKunde,
  onRetry,
  emptyText,
}: OutboundTableProps) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Betreff</TableHead>
            <TableHead>Empfänger</TableHead>
            <TableHead>Kunde</TableHead>
            <TableHead>Herkunft</TableHead>
            <TableHead>Zeit</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((m) => {
            const s = OUT_STATUS[m.status];
            const restrictedTeam = mailTeamMarkerName(m.restrictedToTeam);
            return (
              <TableRow
                key={m.id}
                onClick={() => onOpenDetail(m)}
                className="cursor-pointer"
              >
                <TableCell className="max-w-[20rem]">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-foreground">{m.betreff}</span>
                    {restrictedTeam != null && (
                      <MailTeamBadge team={restrictedTeam} className="shrink-0" />
                    )}
                  </div>
                  {m.status === 'fehler' && m.fehlerGrund && (
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-sem-alert">
                      <AlertTriangle className="size-3 shrink-0" />
                      <span className="truncate">{m.fehlerGrund}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{m.empfaenger}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <EntityLink onActivate={() => onOpenKunde?.(m.kundeId)}>{m.kunde}</EntityLink>
                </TableCell>
                <TableCell>
                  <HerkunftBadge herkunft={m.herkunft} />
                </TableCell>
                <TableCell className="text-muted-foreground">{m.zeit}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <StatusDot tone={s.tone} label={s.label} />
                    {m.status === 'fehler' && onRetry && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRetry(m.id)}
                      >
                        <RotateCw className="size-3.5" />
                        Erneut senden
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {rows.length === 0 && (
        <div className="p-8 text-center text-sm text-muted-foreground">{emptyText}</div>
      )}
    </div>
  );
}
